// Pages/PaymentSuccess.js
//
// Redsys/Getnet success return URL (DS_MERCHANT_URLOK).
//
// IMPORTANT: the browser landing here does NOT prove the payment succeeded — the
// authoritative confirmation is the server-to-server webhook that grants the
// Permission. So this page polls /permissions/check/:productId until access is
// granted (webhook processed) and then links the user into the content.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingPurchase, clearPendingPurchase } from '../utils/redsysCheckout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_TRIES = 10;
const INTERVAL_MS = 1500;

function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | granted | delayed
  const [productId, setProductId] = useState(null);

  useEffect(() => {
    const pending = getPendingPurchase();
    const pid = pending?.productId || null;

    // No payment context (e.g. the URL was opened directly) -> don't show a
    // payment page out of nowhere; send the user home.
    if (!pid) {
      navigate('/', { replace: true });
      return;
    }
    setProductId(pid);

    const authToken = localStorage.getItem('auth-token');
    if (!authToken) {
      setStatus('delayed');
      return;
    }

    let tries = 0;
    let timer;
    let cancelled = false;

    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(`${BACKEND_URL}/permissions/check/${pid}`, {
          headers: { 'auth-token': authToken },
        });
        const data = await res.json();
        if (cancelled) return;

        if (data.hasPermission) {
          clearPendingPurchase();
          setStatus('granted');
          return;
        }
      } catch (_) {
        /* ignore and retry */
      }

      if (cancelled) return;
      if (tries >= MAX_TRIES) {
        setStatus('delayed');
      } else {
        timer = setTimeout(poll, INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [navigate]);

  return (
    <div style={styles.wrap}>
      <style>{'@keyframes ir-spin{to{transform:rotate(360deg)}}'}</style>
      <div style={styles.card}>
        {status === 'checking' && (
          <>
            <div style={styles.spinner} />
            <h1 style={styles.title}>Confirming your payment…</h1>
            <p style={styles.text}>We're activating your access. This only takes a few seconds.</p>
          </>
        )}

        {status === 'granted' && (
          <>
            <div style={styles.check}>✓</div>
            <h1 style={styles.title}>Payment complete!</h1>
            <p style={styles.text}>Your access is now active.</p>
            <button
              style={styles.btn}
              onClick={() => navigate(productId ? `/product/${productId}` : '/')}
            >
              View content
            </button>
          </>
        )}

        {status === 'delayed' && (
          <>
            <div style={styles.check}>✓</div>
            <h1 style={styles.title}>Payment received</h1>
            <p style={styles.text}>
              Your access will be activated shortly. If you don't see it right away,
              refresh your content page in a moment.
            </p>
            <button style={styles.btn} onClick={() => navigate('/')}>
              Go to home
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  card: {
    maxWidth: 420,
    width: '100%',
    textAlign: 'center',
    background: '#15151a',
    border: '1px solid #2a2a33',
    borderRadius: 12,
    padding: '40px 28px',
    color: '#f2f2f5',
  },
  title: { fontSize: 24, margin: '16px 0 8px' },
  text: { color: '#b7b7c0', lineHeight: 1.5, marginBottom: 24 },
  check: {
    width: 56,
    height: 56,
    lineHeight: '56px',
    borderRadius: '50%',
    background: '#1db954',
    color: '#fff',
    fontSize: 28,
    margin: '0 auto',
  },
  spinner: {
    width: 40,
    height: 40,
    margin: '0 auto',
    border: '4px solid #2a2a33',
    borderTopColor: '#1db954',
    borderRadius: '50%',
    animation: 'ir-spin 1s linear infinite',
  },
  btn: {
    background: '#1db954',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    cursor: 'pointer',
  },
};

export default PaymentSuccess;
